import { useEffect, useState } from 'react';
import { LATE_OPTIONS, localIsoDate, normalizeSyncChoice } from '../../lib/quickBooksSync.js';

const SCOPE_OPTIONS = [
  {
    value: 'past_due',
    title: 'Past-due invoices only',
    badge: 'Recommended',
    text: 'Unpaid invoices whose due date has already passed.',
  },
  {
    value: 'all',
    title: 'All unpaid invoices',
    text: 'Every invoice with a balance, including ones that are not due yet.',
  },
];

function cutoffText(choice) {
  if (choice.scope === 'all') return 'We’ll import every unpaid invoice, whatever its due date.';
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - choice.minDaysOverdue);
  const date = cutoff.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  return choice.minDaysOverdue
    ? `We’ll import unpaid invoices that are at least ${choice.minDaysOverdue} days late (due before ${date}).`
    : `We’ll import unpaid invoices that were due before today (${date}).`;
}

const HOW_IT_WORKS = [
  'Each unpaid QuickBooks invoice becomes a demand in DemandJet.',
  'Filled in for you: customer name, billing address, email, amount still owed, currency and invoice number.',
  'A PDF copy of each invoice is added to the demand as evidence.',
  'Files attached to the invoice in QuickBooks (contracts, delivery proof, photos) are added to the demand as evidence.',
  'The invoice needs a billing address with a state. Invoices without one are skipped.',
  'Invoices you already imported are skipped, so nothing is duplicated.',
];

export default function QuickBooksSyncDialog({ initialChoice, onSync, onCancel }) {
  const [choice, setChoice] = useState(() => normalizeSyncChoice(initialChoice));
  const [step, setStep] = useState('how');

  useEffect(() => {
    function onKey(e) {
      if (e.key === 'Escape') onCancel();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  return (
    <div className="social-modal-backdrop" role="presentation" onClick={onCancel}>
      <div
        className="social-modal qb-sync-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="qb-sync-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="social-modal-head">
          <h2 id="qb-sync-title">Sync invoices from QuickBooks</h2>
          <button type="button" className="social-modal-close" onClick={onCancel} aria-label="Close">
            ×
          </button>
        </div>
        {step === 'how' ? (
          <>
            <p className="social-modal-hint">How the QuickBooks sync works:</p>
            <ul className="qb-sync-how">
              {HOW_IT_WORKS.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
            <p className="qb-sync-summary qb-sync-evidence">
              <svg
                className="qb-sync-evidence-icon"
                viewBox="0 0 16 16"
                width="24"
                height="24"
                aria-hidden="true"
              >
                <circle cx="8" cy="8" r="6.5" fill="none" stroke="currentColor" strokeWidth="1.4" />
                <path
                  d="M8 7.2v3.6M8 5.2h.01"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
              <span>
                Attach your evidence to the invoice in QuickBooks before you sync, so there is
                nothing to add by hand afterwards.
              </span>
            </p>
            <div className="social-modal-actions">
              <button type="button" className="btn-ghost" onClick={onCancel}>
                Cancel
              </button>
              <button
                type="button"
                className="btn-primary social-modal-create"
                onClick={() => setStep('options')}
              >
                Next
              </button>
            </div>
          </>
        ) : (
          <>
            <p className="social-modal-hint">
              Choose which unpaid invoices to bring into DemandJet as demands.
            </p>

            <div className="qb-sync-options" role="radiogroup" aria-label="Invoices to import">
              {SCOPE_OPTIONS.map((opt) => {
                const selected = choice.scope === opt.value;
                return (
                  <label key={opt.value} className={`qb-sync-option${selected ? ' is-selected' : ''}`}>
                    <input
                      type="radio"
                      name="qb-sync-scope"
                      value={opt.value}
                      checked={selected}
                      onChange={() => setChoice((c) => ({ ...c, scope: opt.value }))}
                    />
                    <span className="qb-sync-option-body">
                      <span className="qb-sync-option-title">
                        {opt.title}
                        {opt.badge ? <span className="qb-sync-badge">{opt.badge}</span> : null}
                      </span>
                      <span className="qb-sync-option-text">{opt.text}</span>
                    </span>
                  </label>
                );
              })}
            </div>

            {choice.scope === 'past_due' ? (
              <div className="qb-sync-late">
                <p className="qb-sync-late-label" id="qb-sync-late-label">
                  How late should they be?
                </p>
                <div className="qb-sync-chips" role="radiogroup" aria-labelledby="qb-sync-late-label">
                  {LATE_OPTIONS.map((opt) => {
                    const selected = choice.minDaysOverdue === opt.value;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        role="radio"
                        aria-checked={selected}
                        className={`qb-sync-chip${selected ? ' is-selected' : ''}`}
                        onClick={() => setChoice((c) => ({ ...c, minDaysOverdue: opt.value }))}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : null}

            <p className="qb-sync-summary">{cutoffText(choice)}</p>

            <div className="social-modal-actions">
              <button type="button" className="btn-ghost" onClick={() => setStep('how')}>
                Back
              </button>
              <button
                type="button"
                className="btn-primary social-modal-create"
                onClick={() => onSync({ ...choice, asOf: localIsoDate() })}
              >
                Sync invoices
              </button>
            </div>
            <p className="social-modal-note">
              Invoices you already imported are skipped, so nothing is duplicated.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
